import { useQuery } from '@tanstack/react-query'

import { getActiveChainId } from '@/config/active'
import {
  isNutrimaticConfigured,
  NUTRIMATIC_INFINITE_MAINNET_LEDGER,
} from '@/config/nutrimatic'
import { fetchNutrimaticServiceReady } from '@/data/nutrimatic/ready'
import { getNutrimaticSdk } from '@/data/nutrimatic/sdk'

import { MARKET_QUERY_CACHE } from './cachePolicy'
import { marketApiQueryKeys } from './queryKeys'

export type MarketApiAvailability = {
  configured: boolean
  isAvailable: boolean
  isDegraded: boolean
  chainIdMatch: boolean
  chainId?: number
  lagBlocks?: number
  isLoading: boolean
}

export type NutrimaticReadyProbe = {
  ok: boolean
  status: number
}

/** Availability for Nutrimatic-backed market reads (legacy name kept for call sites). */
export function resolveMarketApiAvailability(params: {
  configured: boolean
  activeChainId: number
  ledgerChainId: number
  ready?: NutrimaticReadyProbe
  readyLoading?: boolean
  sdkReady?: boolean
}): MarketApiAvailability {
  const {
    configured,
    activeChainId,
    ledgerChainId,
    ready,
    readyLoading = false,
    sdkReady = false,
  } = params

  const chainIdMatch = activeChainId === ledgerChainId
  const probeOk = ready?.ok === true
  const isDegraded = configured && ready != null && !probeOk && ready.status > 0
  const canServeReads = configured && sdkReady && (probeOk || isDegraded)
  const isAvailable = Boolean(canServeReads && chainIdMatch)

  return {
    configured,
    isAvailable,
    isDegraded,
    chainIdMatch,
    chainId: ledgerChainId,
    isLoading: configured && readyLoading,
  }
}

export function useMarketApiStatus(): MarketApiAvailability {
  const configured = isNutrimaticConfigured()
  const activeChainId = getActiveChainId()
  const ledgerChainId = Number(NUTRIMATIC_INFINITE_MAINNET_LEDGER.chainId)
  const sdkReady = configured && getNutrimaticSdk() != null

  const readyQuery = useQuery({
    queryKey: marketApiQueryKeys.status(),
    queryFn: async (): Promise<NutrimaticReadyProbe> => {
      const result = await fetchNutrimaticServiceReady()
      return { ok: result.ok, status: result.status }
    },
    enabled: configured,
    retry: 1,
    ...MARKET_QUERY_CACHE.status,
  })

  return resolveMarketApiAvailability({
    configured,
    activeChainId,
    ledgerChainId,
    ready: readyQuery.data,
    readyLoading: readyQuery.isLoading,
    sdkReady,
  })
}
